import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Code, Cpu, Settings, CheckCircle, Brain, Cloud, Users, Shield } from 'lucide-react';

const SoftwareDevelopmentPage = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const features = [
    {
      title: 'Customised Critical Software',
      description: 'Tailored solutions for mission-critical operations in defense, intelligence, and cybersecurity domains. Our secure development practices ensure reliability, scalability, and compliance with stringent security standards for classified and sensitive systems.',
      icon: Shield,
      image: 'https://images.unsplash.com/photo-1607799279861-4dd421887fb3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxzb2Z0d2FyZSUyMGRldmVsb3BtZW50fGVufDB8fHxibHVlfDE3NTIyNDExMTR8MA&ixlib=rb-4.1.0&q=85',
    },
    {
      title: 'Advanced AI-Powered Applications',
      description: 'Cutting-edge artificial intelligence solutions for threat detection, intelligence analysis, and predictive security. Our AI applications leverage machine learning, natural language processing, and computer vision for enhanced operational capabilities.',
      icon: Brain,
      image: 'https://images.unsplash.com/photo-1590494165264-1ebe3602eb80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxjeWJlcnNlY3VyaXR5fGVufDB8fHxibHVlfDE3NTIxNTE0MTR8MA&ixlib=rb-4.1.0&q=85',
    },
    {
      title: 'Cloud-Native & Edge Solutions',
      description: 'Secure cloud architectures and edge computing solutions designed for government and enterprise environments. Our hybrid cloud strategies ensure data sovereignty, compliance, and optimal performance across distributed systems.',
      icon: Cloud,
      image: 'https://images.pexels.com/photos/7663144/pexels-photo-7663144.jpeg',
    },
    {
      title: 'Secure DevOps & Automation',
      description: 'DevSecOps practices integrating security throughout the development lifecycle. Our automated CI/CD pipelines, infrastructure as code, and security testing ensure rapid, secure delivery while maintaining compliance and audit trails.',
      icon: Settings,
      image: 'https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxzb2Z0d2FyZSUyMGRldmVsb3BtZW50fGVufDB8fHxibHVlfDE3NTIyNDExMTR8MA&ixlib=rb-4.1.0&q=85',
    },
    {
      title: 'Collaborative Agile Teams',
      description: 'Expert development teams with security clearances and domain expertise in cybersecurity, intelligence, and defense applications. Our agile methodologies ensure transparency, adaptability, and continuous stakeholder engagement.',
      icon: Users,
      image: 'https://images.pexels.com/photos/5475750/pexels-photo-5475750.jpeg',
    },
    {
      title: 'Legacy System Modernization',
      description: 'Secure migration and modernization of legacy systems while maintaining operational continuity. Our phased approach ensures zero-downtime transitions, data integrity, and enhanced security posture for critical infrastructure.',
      icon: Cpu,
      image: 'https://images.pexels.com/photos/8728559/pexels-photo-8728559.jpeg',
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1519389950473-47ba0277781c')`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-near-black/90 z-0" />
        <div className="absolute inset-0 grid-pattern opacity-20 z-1" />
        <div className="container relative z-10">
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-4xl">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-16 flex items-center justify-center">
                <Code className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="heading-font text-4xl md:text-6xl font-bold text-off-white">
                  Software <span className="text-royal-gold">Development</span>
                </h1>
              </div>
            </div>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl">
              Specialized software development for defense, intelligence, and cybersecurity domains. Our secure development practices deliver mission-critical applications with AI integration, cloud-native architectures, and DevSecOps methodologies tailored for government and enterprise security requirements.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-gray-dark" ref={ref}>
        <div className="container">
          <div className="space-y-20">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-16 flex items-center justify-center">
                      <feature.icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="heading-font text-3xl md:text-4xl font-bold text-off-white">
                        {feature.title}
                      </h3>
                    </div>
                  </div>
                  <p className="text-lg text-gray-300 mb-8 leading-relaxed">{feature.description}</p>
                </div>
                {/* Image */}
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div className="relative rounded-24 overflow-hidden group">
                    <img src={feature.image} alt={feature.title} className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-near-black/60 to-transparent" />
                    <div className="absolute bottom-6 left-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-12 flex items-center justify-center">
                        <feature.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default SoftwareDevelopmentPage; 